/**
 * Button component - кнопка/ссылка для секций
 * Работает в React и в статике
 */
import { SvgIcon } from '../../utils/icons';

// Базовые классы для всех кнопок
const baseClasses = `
  inline-flex items-center justify-center
  font-medium rounded-lg
  transition-all duration-200
  cursor-pointer
  disabled:opacity-50 disabled:cursor-not-allowed
`.trim().replace(/\s+/g, ' ');

// Варианты кнопок
const variantClasses = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  secondary: 'bg-secondary text-secondary-foreground hover:opacity-90',
  outline: 'border-2 border-primary text-primary hover:bg-primary/10',
  'outline-white': 'border-2 border-white text-white hover:bg-white/20',
  ghost: 'text-primary hover:bg-primary/10',
  'ghost-white': 'text-white hover:bg-white/20',
  link: 'text-primary underline-offset-4 hover:underline p-0',
};

// Размеры кнопок
const sizeClasses = {
  sm: 'px-4 py-2 text-sm gap-1.5',
  md: 'px-6 py-3 text-base gap-2',
  lg: 'px-8 py-4 text-lg gap-2.5',
};

// Размеры иконок соответствующие размеру кнопки
const iconSizes = {
  sm: 16,
  md: 18,
  lg: 20,
};

export default function Button({
  children,
  href,              // Если есть - рендерим <a>, иначе <button>
  variant = 'primary', // primary, secondary, outline, outline-white, ghost, ghost-white, link
  size = 'md',       // sm, md, lg
  icon,              // Имя иконки (слева)
  iconRight,         // Имя иконки (справа)
  fullWidth = false, // На всю ширину
  className = '',
  disabled = false,
  target,            // Для ссылок: _blank и т.д.
  rel,               // Для ссылок: noopener и т.д.
  onClick,
  type = 'button',   // Для button: button, submit
  ...props
}) {
  // Собираем классы
  const classes = [
    baseClasses,
    variantClasses[variant] || variantClasses.primary,
    sizeClasses[size] || sizeClasses.md,
    fullWidth ? 'w-full' : '',
    className,
  ].filter(Boolean).join(' ');

  const iconSize = iconSizes[size] || iconSizes.md;

  // Контент кнопки
  const content = (
    <>
      {icon && <SvgIcon name={icon} size={iconSize} />}
      {children}
      {iconRight && <SvgIcon name={iconRight} size={iconSize} />}
    </>
  );

  // Рендерим <a> или <button>
  if (href) {
    return (
      <a
        href={href}
        className={classes}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : rel}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {content}
    </button>
  );
}

// Экспорт вариантов для использования в настройках
Button.variants = Object.keys(variantClasses);
Button.sizes = Object.keys(sizeClasses);
